import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Loader2, CheckCircle, Briefcase, MapPin, Calendar, DollarSign, AlertCircle, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AcceptOfferPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [alreadyAccepted, setAlreadyAccepted] = useState(false);
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    fetchOfferDetails();
  }, [token]);

  const fetchOfferDetails = async () => {
    try {
      const response = await fetch(`${API_URL}/api/employee/accept-offer/${token}`);
      const data = await response.json();
      
      if (data.success) {
        if (data.already_accepted) {
          setAlreadyAccepted(true);
        } else {
          setEmployee(data.employee);
        }
      } else {
        setError(data.detail || 'Invalid or expired link');
      }
    } catch (error) {
      setError('Error loading offer details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptOffer = async () => {
    setIsAccepting(true);
    try {
      const response = await fetch(`${API_URL}/api/employee/accept-offer/${token}`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.success) {
        setAccepted(true);
      } else {
        setError(data.detail || 'Error accepting offer');
      }
    } catch (error) {
      setError('Error accepting offer');
    } finally {
      setIsAccepting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center" data-testid="loading-spinner">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative w-full max-w-lg">
        {/* Back to Home Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to Website</span>
        </Link>

        {/* Error State */}
        {error && (
          <Card className="shadow-2xl bg-white/95 backdrop-blur-sm">
            <CardContent className="pt-8 text-center">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Invalid Link</h2>
              <p className="text-gray-500 mb-6">{error}</p>
              <Button onClick={() => navigate('/')} variant="outline">
                Go to Home
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Already Accepted */}
        {alreadyAccepted && !error && (
          <Card className="shadow-2xl bg-white/95 backdrop-blur-sm">
            <CardContent className="pt-8 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Offer Already Accepted</h2>
              <p className="text-gray-500 mb-6">You have already accepted this offer. Please check your email for login credentials.</p>
              <Button onClick={() => navigate('/employee/login')} className="bg-blue-600 hover:bg-blue-700">
                Go to Login
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Accepted Success */}
        {accepted && (
          <Card className="shadow-2xl bg-white/95 backdrop-blur-sm">
            <CardContent className="pt-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-green-600 mb-2">Offer Accepted!</h2>
              <p className="text-gray-500 mb-6">
                Congratulations! Check your email for login credentials to complete your onboarding.
              </p>
              <Button onClick={() => navigate('/employee/login')} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="go-to-login-btn">
                Go to Employee Login
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Offer Details */}
        {employee && !accepted && !error && !alreadyAccepted && (
          <Card className="shadow-2xl bg-white/95 backdrop-blur-sm overflow-hidden">
            <CardHeader className="text-center bg-gradient-to-r from-blue-600 to-teal-500 text-white">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-2xl">Employment Offer</CardTitle>
              <CardDescription className="text-white/80">STEAM-IE Adventures Pvt. Ltd.</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold">{employee.name}</h3>
                <p className="text-blue-600 font-medium">{employee.position}</p>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Briefcase className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-500">Department</p>
                    <p className="font-medium">{employee.department}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="font-medium">{employee.location}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-500">Start Date</p>
                    <p className="font-medium">{employee.date_of_joining}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-sm text-gray-500">Annual CTC</p>
                    <p className="font-medium text-green-600">₹{employee.ctc?.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <p className="text-sm text-blue-700">
                  By accepting this offer, you agree to join STEAM-IE Adventures Pvt. Ltd. and complete the onboarding process.
                </p>
              </div>

              <Button 
                className="w-full bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600" 
                onClick={handleAcceptOffer}
                disabled={isAccepting}
                data-testid="accept-offer-btn"
              >
                {isAccepting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Accept Offer
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-gray-500 text-xs mt-6">
          © {new Date().getFullYear()} STEAM-IE. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default AcceptOfferPage;
